/**
 * PendingApprovalsWidget Component
 * =================================
 * Phase 7: Shows pending approval items for reviewers on the Dashboard
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Clock, CheckCircle, Image, Calendar, ArrowRight, Loader2, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PendingApprovalsWidget = () => {
  const navigate = useNavigate();
  const [pendingItems, setPendingItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPendingApprovals = async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const [pendingRes, summaryRes] = await Promise.all([
          axios.get(`${API}/approvals/pending`, { headers }),
          axios.get(`${API}/approvals/summary`, { headers })
        ]);
        
        setPendingItems(pendingRes.data.items || []);
        setSummary(summaryRes.data);
      } catch (error) {
        console.error('Failed to fetch pending approvals:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPendingApprovals();
  }, []);

  const handleItemClick = (item) => {
    if (item.entity_type === 'design') {
      navigate(`/marketing-hub/content-studio?design=${item.id}`);
    } else {
      navigate(`/marketing-hub/scheduler?event=${item.id}`);
    }
  };

  return (
    <Card 
      className="dark:bg-slate-800/60 dark:border-slate-700 border-l-4 border-l-yellow-500 hover:shadow-lg transition-shadow"
      data-testid="pending-approvals-widget"
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between dark:text-white">
          <span className="flex items-center gap-2 text-base">
            <Clock className="h-5 w-5 text-yellow-500" />
            Pending Approvals
          </span>
          {summary?.total_pending > 0 && (
            <Badge className="bg-yellow-500 text-white">
              {summary.total_pending}
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="dark:text-slate-400">Items awaiting your review</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-yellow-500" />
          </div>
        ) : pendingItems.length === 0 ? (
          <div className="text-center py-6">
            <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">All caught up!</p>
            <p className="text-xs text-muted-foreground mt-1">No items pending approval</p>
          </div>
        ) : (
          <ScrollArea className="h-64">
            <div className="space-y-2 pr-2">
              {pendingItems.slice(0, 10).map((item) => (
                <div
                  key={`${item.entity_type}-${item.id}`}
                  className="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                  onClick={() => handleItemClick(item)}
                >
                  <div className="flex items-start gap-3">
                    {/* Thumbnail or Icon */}
                    <div className="w-10 h-10 rounded bg-slate-200 dark:bg-slate-600 overflow-hidden flex-shrink-0 flex items-center justify-center">
                      {item.thumbnail_url ? (
                        <img 
                          src={item.thumbnail_url.startsWith('http') ? item.thumbnail_url : `${BACKEND_URL}${item.thumbnail_url}`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : item.entity_type === 'design' ? (
                        <Image className="h-5 w-5 text-slate-400" />
                      ) : (
                        <Calendar className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium dark:text-white truncate">{item.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs capitalize">
                          {item.entity_type}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {item.submitted_by?.name || 'Unknown'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {item.submitted_at 
                          ? formatDistanceToNow(new Date(item.submitted_at), { addSuffix: true })
                          : ''}
                      </p>
                    </div>
                    
                    <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
        
        {/* Summary Stats */}
        {summary && (summary.designs.pending > 0 || summary.events.pending > 0) && (
          <div className="mt-3 pt-3 border-t dark:border-slate-700 flex gap-4 text-xs">
            <div className="flex items-center gap-1">
              <Image className="h-3 w-3 text-blue-500" />
              <span className="text-muted-foreground">{summary.designs.pending} designs</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3 text-purple-500" />
              <span className="text-muted-foreground">{summary.events.pending} events</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PendingApprovalsWidget;
